(ns app.front.view.party.business
  (:require [app.domain.business.model :as business-model]
            [app.front.form.personal-id-render :as personal-id-render]
            [app.front.process :as process]
            [app.front.ui.action-buttons :as ui-action-buttons]
            [app.front.ui.radio :as ui-radio]
            [app.front.util :refer [for-frag]]
            [app.front.view.business.information :as business-info-form]
            [app.front.view.business.search :as business-search]
            [app.front.view.party.common :as common]
            [app.front.view.party.subs :as party-subs]
            [app.front.view.workspace.subs :as ws-subs]
            [clojure.string :as str]
            [core.front.carbon :as cc]
            [core.front.config :as front-config]
            [core.front.event :refer [<sub >evt]]
            [core.front.icons :as i]
            [core.util.util :as util]
            [potpuri.core :as p]
            [re-frame.core-instrumented :as rf]
            [schema-tools.core :as st]
            [schema.core :as s]))


(rf/reg-sub ::data :-> ::data)


(rf/reg-event-fx ::init
  (fn [{:keys [db]} _]
    {:db       (dissoc db ::data)
     :dispatch [::business-search/init]}))


(rf/reg-sub ::business-party-unique-ids
  (fn [[_ phase-id]]
    (rf/subscribe [::party-subs/parties phase-id]))
  (fn [parties _]
    (->> parties
         (keep :party/business)
         (map :business/unique-id)
         set)))


(rf/reg-sub ::my-businesses
  (fn [[_ phase-id]]
    [(rf/subscribe [::data])
     (rf/subscribe [:language])
     (rf/subscribe [::business-party-unique-ids phase-id])])
  (fn [[data lang existing-ids] _]
    (->> data
         ::my-businesses
         (remove (comp existing-ids :business/unique-id))
         (sort-by #(-> % (business-model/business-name lang) str/lower-case)))))

(rf/reg-sub ::all-my-businesses
  ;; Every business of the user, also the ones already added as a party to some phase.
  :<- [::data]
  :-> ::my-businesses)


(rf/reg-event-fx ::fetch-my-businesses
  (fn [_ _]
    {:comm/command {:command    :business/my-businesses
                    :method     :get
                    :on-success [::fetch-my-businesses.success]}}))


(rf/reg-event-db ::fetch-my-businesses.success
  (fn [db [_ _ results]]
    (assoc-in db [::data ::my-businesses] results)))


(rf/reg-event-fx ::add-business
  (fn [{:keys [db]} [_ target opts]]
    (let [form       (::common/document db)
          authority? (-> target :granted-permissions :permission.application/review)
          owner-type (or (:business/owner-type form)
                         (if authority?
                           :business.owner-type/other
                           :business.owner-type/self))]
      {:dispatch [::process/signal
                  target
                  (if (::add-new-business? form)
                    [:process.signal/add-new-business
                     (-> (select-keys form business-model/new-business-req-attrs)
                         (assoc :business/owner-type owner-type)
                         ;; Callers (e.g. the application wizard) can defer the owner invite:
                         ;; store the email without sending it. Omitted => backend default (invite now).
                         (p/assoc-if :party/send-invite? (:send-invite? opts)))]
                    [:process.signal/add-existing-business
                     (select-keys form business-model/existing-business-req-attrs)])
                  {:notify      {:success :changes-saved}
                   :on-success  [::add-business.success (:on-success opts)]
                   :track-save? true}]})))


(rf/reg-event-fx ::add-business.success
  (fn [{:keys [db]} [_ on-success]]
    {:db         (common/reset-db db)
     ;; A newly created business must become available to applicant selectors immediately.
     :dispatch-n (cond-> [[::common/close]
                          [::fetch-my-businesses]]
                   on-success (conj on-success))}))


(rf/reg-event-fx ::select-existing-business
  (fn [_ [_ business]]
    {:fx [[:dispatch [::common/set-doc-value [:business/unique-id] (:business/unique-id business)]]
          ;; also update the business-search display
          [:dispatch [::business-search/select-existing-business business]]]}))


(rf/reg-sub ::personal-id-schema
  :<- [::personal-id-render/validator-fn]
  (fn [valid? _]
    (some-> valid?
            (s/pred 'valid-personal-id))))


(rf/reg-sub ::schema
  :<- [::common/document [::add-new-business?]]
  :<- [::personal-id-schema]
  (fn [[add-new? personal-id-schema] _]
    (if add-new?
      (if personal-id-schema
        (st/assoc business-model/NewBusinessRequest
                  (s/optional-key :business/personal-id)
                  personal-id-schema)
        business-model/NewBusinessRequest)
      business-model/ExistingBusinessRequest)))


(rf/reg-sub ::checker-fn
  :<- [::schema]
  :-> s/checker)


(rf/reg-sub ::document
  :<- [::common/document]
  :<- [::common/document [::add-new-business?]]
  (fn [[document add-new?] _]
    (select-keys document (if add-new?
                            business-model/new-business-req-attrs
                            business-model/existing-business-req-attrs))))


(defn- masked-personal-id? [value]
  (and (string? value)
       (str/includes? value "*-")))


(defn- check-personal-id
  "Country-specific personal-id check shared by the add (`validator-fn`) and edit
   (`validate-edit-field`) validators: a non-blank unmasked value must pass `personal-id-valid?`.
   Returns a schema-error keyword or nil."
  [personal-id-valid? value]
  (when (and personal-id-valid?
             (not (str/blank? value))
             (not (masked-personal-id? value))
             (not (personal-id-valid? value)))
    :form-field/personal-id-error))


(defn validator-fn [[schema document personal-id-valid?] & _]
  (fn [field]
    (if (= field :business/personal-id)
      (check-personal-id personal-id-valid? (get document field))
      (s/check (st/select-keys schema [field]) (select-keys document [field])))))


(rf/reg-sub ::validator-fn
  :<- [::schema]
  :<- [::document]
  :<- [::personal-id-render/validator-fn]
  validator-fn)


(rf/reg-sub ::form-valid?
  :<- [::checker-fn]
  :<- [::document]
  (fn [[checker document] _]
    (nil? (checker document))))


;; Editing an existing business (e.g. in the application wizard) validates against
;; BusinessEditInfo only - the party-role/invite fields of the add flow do not apply.

(rf/reg-sub ::edit-schema
  :<- [::personal-id-schema]
  (fn [personal-id-schema _]
    (if personal-id-schema
      (st/assoc business-model/BusinessEditInfo
                (s/optional-key :business/personal-id)
                personal-id-schema)
      business-model/BusinessEditInfo)))


(rf/reg-sub ::edit-document
  :<- [::common/document]
  (fn [document _]
    (select-keys document business-model/business-attrs)))


(rf/reg-sub ::edit-validator-fn
  :<- [::edit-schema]
  :<- [::edit-document]
  :<- [::personal-id-render/validator-fn]
  validator-fn)


(defn validate-edit-field
  "Validate a projected business field `value` against `BusinessEditInfo`, mirroring `validator-fn`
   but for an explicit (post-edit) value rather than the current document. Returns a truthy schema
   error or nil when valid. The country-specific personal-id check is handled separately, exactly as
   in `validator-fn`; for all other fields `BusinessEditInfo` and `::edit-schema` are identical."
  [personal-id-valid? field value]
  (if (= field :business/personal-id)
    (check-personal-id personal-id-valid? value)
    (business-model/check-edit-field field value)))


(rf/reg-sub ::edit-field-validator-fn
  :<- [::personal-id-render/validator-fn]
  (fn [personal-id-valid? _]
    (partial validate-edit-field personal-id-valid?)))


(defn available-extra-permissions [workspace]
  ;; CE does not allow any extra permissions for parties
  (when (-> workspace :workspace/permit-type (not= :permit-type/CE))
    (concat [:party/modify-application
             :party/view-other-phases]
            (when (-> workspace :entity/parent)
              ;; has project
              [:party/add-application]))))


(defn add-select-buttons-content [{:keys [tr target disabled?]}]
  (let [tr-key (if (<sub [::common/document [::add-new-business?]])
                 :party-modal/add-new-business-button
                 :party-modal/add-selected-business-button)
        valid? (<sub [::form-valid?])]
    [ui-action-buttons/action-buttons {:buttons [{:icon     i/remove
                                                  :text     (tr [:party/cancel-button])
                                                  :on-click #(>evt [::common/close])}
                                                 {:icon      i/user-plus
                                                  :on-click  #(>evt [::add-business target])
                                                  :disabled? (or disabled? (not valid?))
                                                  :text      (tr [tr-key])}]}]))


(defn business-form [tr target & [{:keys [light hide-role-selector?]}]]
  (let [lang            (<sub [:language])
        ctx             (util/mmap lang tr)
        target-id       (:db/id target)
        add-new-mode?   (<sub [::common/document [::add-new-business?]])
        validator       (<sub [::validator-fn])
        schema          (<sub [::schema])
        party-roles     (<sub [::common/document-party-roles])
        extra-perms     (-> target :entity/parent available-extra-permissions)
        modify-roles    (common/roles-requiring-modify-application (<sub [::ws-subs/required-so-roles]))
        on-role-toggle  (if (contains? (set extra-perms) :party/modify-application)
                          (common/role-toggle-change-permissions-fn
                            modify-roles
                            (map :party-role/key party-roles)
                            :party/modify-application)
                          (constantly nil))
        my-businesses   (<sub [::my-businesses target-id])
        authority?      (-> target :granted-permissions :permission.phase/authority-read)
        owner-type      (or (<sub [::common/document [:business/owner-type]])
                            :business.owner-type/self)
        on-change       #(>evt [::common/set-doc-value [%1] %2])
        on-change-sync  #(rf/dispatch-sync (if (and (string? %2) (str/blank? %2))
                                             [::common/dissoc-doc-path [%1]]
                                             [::common/set-doc-value [%1] %2]))
        value-fn        #(<sub [::common/document [%]])
        default-country (<sub [::front-config/country-kw])
        default-subdiv  (-> target :entity/parent :workspace/municipality :municipality/subdivision)]
    [:div
     [:div
      (str (tr [(cond
                  authority? :party-modal/add-business-help
                  (seq my-businesses) :party-modal/add-business-applicant-help
                  :else :party-modal/add-business-applicant-help-new-only)])
           " "
           (tr [:party-modal/add-business-help-2]))]
     (if (<sub [::business-search/selected-business])
       [:div.flex
        [:div.max-w-sm [business-search/selected-business-display (util/mmap lang tr)]]
        (when extra-perms
          [:div.flex-1.ml-4.mt-2
           [common/additional-permissions (util/mmap {:permissions extra-perms} tr target)]])]
       [:<>
        [:div.flex
         [:div.my-4
          {:class (when add-new-mode?
                    "max-w-sm")}
          (cc/checkbox {:id         "add-new-business-checkbox"
                        :label-text (tr [:party-modal/add-new-business-label])
                        :checked    (boolean add-new-mode?)
                        :on-change  (fn [value]
                                      (>evt [::common/set-doc-value [::add-new-business?] value])
                                      (>evt [::common/set-doc-value [:business/mailing-country] default-country])
                                      (>evt [::common/set-doc-value [:business/subdivision] default-subdiv])
                                      (>evt [::common/reset-manual-party-fields]))})]
         (when (and add-new-mode? extra-perms)
           [:div.flex-1.ml-4.mt-4
            [common/additional-permissions (util/mmap {:permissions extra-perms} tr target)]])]
        (if add-new-mode?
          [:<>
           [business-info-form/business-info-form
            (util/mmap validator tr schema on-change on-change-sync value-fn light)]
           [:div.mt-3
            [:div (tr [:party-modal/invite-business-owner])]
            (when-not authority?
              [:div.mt-3
               [ui-radio/radio {:name      "business-owner-type"
                                :values    [:business.owner-type/self
                                            :business.owner-type/other]
                                :value     owner-type
                                :val->str  (comp tr vector)
                                :on-change #(>evt [::common/set-doc-value [:business/owner-type] %])}]])
            (when (or authority?
                      (= owner-type :business.owner-type/other))
              [:div.mt-2.w-full.md:w-1-2
               [common/email-input {:class-name "invite-email"
                                    :tr         tr
                                    :validator  validator
                                    :light      light
                                    :field      :business/invite-email
                                    :label      :business/invite-email}]])]]
          (cond
            authority?
            [business-search/authority-existing-business-search
             (util/mmap {:hide-businesses-sub [::business-party-unique-ids target-id]
                         :on-select           #(>evt [::select-existing-business %])}
                        tr lang)]

            (seq my-businesses)
            [:div
             [:div.mb-2 (tr [:party-modal/select-existing-business])]
             [:ul.border-b-2.border-ui-night-sky-50.border-solid
              (for-frag [business my-businesses]
                [:li.p-2.hover:bg-gray-300.cursor-pointer.border-2.border-b-0.border-ui-night-sky-50.border-solid
                 {:role     "button"
                  :on-click #(>evt [::select-existing-business business])}
                 [business-search/business-display-name ctx business]])]]))])
     (when-not hide-role-selector?
       [:div.flex.mt-4
        [:div.w-full
         [common/role-selector (util/mmap {:on-toggle     on-role-toggle
                                           :legend-tr-key :party-modal/business-role-legend}
                                          tr validator target-id)]]])]))
